import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCartAbandonments, sendCartReminder } from '../../store/actions';
import { 
  Container, Row, Col, Card, CardBody, Table, Button, 
  Spinner, Badge, Modal, ModalHeader, ModalBody, ModalFooter, 
  FormGroup, Label, Input, UncontrolledTooltip, Dropdown, 
  DropdownToggle, DropdownMenu, DropdownItem, Alert
} from 'reactstrap';
import { formatArabicDate } from '../Utility/dateUtils';
import { FiSend, FiClock, FiShoppingCart, FiUser, FiPhone, FiDollarSign, FiRefreshCw } from 'react-icons/fi';
import './CartAbandonment.css'; // Create this CSS file for custom styles

const CartAbandonmentPage = () => {
  const dispatch = useDispatch();
  const { 
    abandonments = [], 
    loading = false, 
    error = null, 
    sendingReminders = {} 
  } = useSelector(state => state.cartAbandonment || {});

  // State management
  const [modalOpen, setModalOpen] = useState(false);
  const [currentCart, setCurrentCart] = useState(null);
  const [useDefaultMessage, setUseDefaultMessage] = useState(true);
  const [customMessage, setCustomMessage] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [timeThreshold, setTimeThreshold] = useState(24); // Default 24 hours
  const [successAlert, setSuccessAlert] = useState(null);
  const [searchTerm, setSearchTerm] = useState(""); // new: search
  const COOLDOWN_MS = 3 * 60 * 60 * 1000; // 3 hours

  const defaultMessage = "لا تترك عربتك! أكمل عملية الدفع الآن لتأمين طلبك";

  // Filter by search term (username or mobile)
  const filteredAbandonments = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return abandonments;
    return abandonments.filter(a => {
      const username = (a.user?.username || "").toLowerCase();
      const mobile = (a.user?.mobile || "").toLowerCase();
      return username.includes(term) || mobile.includes(term);
    });
  }, [abandonments, searchTerm]);

  const canSendReminder = (ab) => {
    if (!ab.lastReminderSent) return true;
    const last = new Date(ab.lastReminderSent).getTime();
    return Date.now() - last > COOLDOWN_MS;
  };

  const nextAllowedAt = (ab) => {
    if (!ab.lastReminderSent) return null;
    const last = new Date(ab.lastReminderSent).getTime();
    return new Date(last + COOLDOWN_MS);
  };

  useEffect(() => {
    dispatch(fetchCartAbandonments({ timeThreshold }));
  }, [dispatch, timeThreshold]);

  const handleSendReminder = (abandonment) => {
    setCurrentCart(abandonment);
    setModalOpen(true);
  };

  const confirmSendReminder = () => {
    if (!currentCart) return;
    if (!canSendReminder(currentCart)) {
      const next = nextAllowedAt(currentCart);
      setModalOpen(false);
      setSuccessAlert(`لا يمكن الإرسال الآن. حاول بعد ${formatArabicDate(next)}`);
      setTimeout(() => setSuccessAlert(null), 5000);
      return;
    }
    
    const message = useDefaultMessage ? defaultMessage : customMessage;
    
    dispatch(sendCartReminder({
      userId: currentCart.app_user_id,
      cartId: currentCart.cartId,
      message: message
    }));
    
    setModalOpen(false);
    setCustomMessage('');
    setUseDefaultMessage(true);
    setSuccessAlert(`تم إرسال التذكير إلى ${currentCart.user?.username || 'العميل'}`);
    setTimeout(() => setSuccessAlert(null), 5000);
  };

  const toggleDropdown = () => setDropdownOpen(prev => !prev);

  const handleTimeThresholdChange = (hours) => {
    setTimeThreshold(hours);
    setDropdownOpen(false);
  };

  const calculateAbandonmentRate = () => {
    if (abandonments.length === 0) return 0;
    const totalAmount = abandonments.reduce((sum, cart) => sum + (cart.totalAmount || 0), 0);
    return totalAmount.toFixed(2);
  };

  const sendAllEligible = () => {
    filteredAbandonments.forEach(ab => {
      if (canSendReminder(ab)) {
        const message = defaultMessage;
        dispatch(sendCartReminder({
          userId: ab.app_user_id,
          cartId: ab.cartId,
          message
        }));
      }
    });
    setSuccessAlert("تم إرسال التذكيرات المؤهلة.");
    setTimeout(() => setSuccessAlert(null), 5000);
  };

  return (
    <div className="page-content" dir="rtl">
      <Container fluid>
        {successAlert && (
          <Alert color="success" className="mt-3">
            {successAlert}
          </Alert>
        )}
        
        <Row className="mb-4">
          <Col lg="12">
            <div className="page-title-box d-flex align-items-center justify-content-between">
              <h4 className="mb-0">العملاء الذين لم يكملوا الدفع</h4>
              <div className="d-flex align-items-center">
                {/* Search input */}
                <Input
                  type="search"
                  placeholder="ابحث باسم العميل أو رقم الجوال"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="me-3"
                  style={{ maxWidth: 280 }}
                />
                <Dropdown isOpen={dropdownOpen} toggle={toggleDropdown}>
                  <DropdownToggle caret color="light" className="d-flex align-items-center">
                    <FiClock className="me-2" />
                    عرض العربات المتروكة منذ: {timeThreshold} ساعة
                  </DropdownToggle>
                  <DropdownMenu>
                    <DropdownItem onClick={() => handleTimeThresholdChange(6)}>6 ساعات</DropdownItem>
                    <DropdownItem onClick={() => handleTimeThresholdChange(12)}>12 ساعة</DropdownItem>
                    <DropdownItem onClick={() => handleTimeThresholdChange(24)}>24 ساعة</DropdownItem>
                    <DropdownItem onClick={() => handleTimeThresholdChange(48)}>48 ساعة</DropdownItem>
                  </DropdownMenu>
                </Dropdown>
                <Button 
                  color="primary" 
                  className="ms-3 d-flex align-items-center"
                  onClick={() => dispatch(fetchCartAbandonments({ timeThreshold }))}
                  disabled={loading}
                >
                  <FiRefreshCw className="me-2" />
                  تحديث
                </Button>
                <Button
                  color="success"
                  className="ms-3"
                  onClick={sendAllEligible}
                  disabled={loading || filteredAbandonments.length === 0}
                >
                  إرسال تذكير للجميع
                </Button>
              </div>
            </div>
          </Col>
        </Row>

        <Row>
          <Col md="4">
            <Card className="stat-card">
              <CardBody>
                <div className="d-flex align-items-center">
                  <div className="avatar-sm me-3">
                    <span className="avatar-title bg-primary rounded-circle">
                      <FiShoppingCart className="font-size-20" />
                    </span>
                  </div>
                  <div>
                    <h5 className="mb-1">{abandonments.length}</h5>
                    <p className="text-muted mb-0">عربة متروكة</p>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col md="4">
            <Card className="stat-card">
              <CardBody>
                <div className="d-flex align-items-center">
                  <div className="avatar-sm me-3">
                    <span className="avatar-title bg-success rounded-circle">
                      <FiDollarSign className="font-size-20" />
                    </span>
                  </div>
                  <div>
                    <h5 className="mb-1">{calculateAbandonmentRate()} ر.س</h5>
                    <p className="text-muted mb-0">إجمالي القيمة</p>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col md="4">
            <Card className="stat-card">
              <CardBody>
                <div className="d-flex align-items-center">
                  <div className="avatar-sm me-3">
                    <span className="avatar-title bg-warning rounded-circle">
                      <FiUser className="font-size-20" />
                    </span>
                  </div>
                  <div>
                    <h5 className="mb-1">
                      {new Set(abandonments.map(a => a.app_user_id)).size}
                    </h5>
                    <p className="text-muted mb-0">عملاء مختلفين</p>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col lg="12">
            <Card>
              <CardBody>
                {loading && (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                    <p className="mt-2">جاري تحميل البيانات...</p>
                  </div>
                )}
                
                {error && (
                  <Alert color="danger">
                    <p>حدث خطأ: {error}</p>
                    <Button 
                      color="danger" 
                      size="sm" 
                      onClick={() => dispatch(fetchCartAbandonments({ timeThreshold }))}
                    >
                      حاول مرة أخرى
                    </Button>
                  </Alert>
                )}
                
                {!loading && !error && filteredAbandonments.length === 0 && (
                  <div className="text-center py-5">
                    <FiShoppingCart size={48} className="text-muted mb-3" />
                    <h5>لا توجد عربات تسوق متروكة حالياً</h5>
                    <p className="text-muted">سيتم عرض العربات غير المكتملة هنا</p>
                  </div>
                )}
                
                {!loading && !error && filteredAbandonments.length > 0 && (
                  <div className="table-responsive">
                    <Table hover className="table-centered table-nowrap mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>#</th>
                          <th>
                            <FiUser className="me-2" />
                            العميل
                          </th>
                          <th>
                            <FiPhone className="me-2" />
                            رقم الجوال
                          </th>
                          <th>عدد العناصر</th>
                          <th>المبلغ الإجمالي</th>
                          <th>آخر تحديث</th>
                          <th>آخر تذكير</th>
                          <th>الإجراءات</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredAbandonments.map((abandonment, index) => (
                          <tr key={abandonment.cartId || index}>
                            <td>{index + 1}</td>
                            <td>
                              <div className="d-flex align-items-center">
                                <div className="avatar-xs me-2">
                                  <span className="avatar-title bg-light rounded-circle">
                                    {abandonment.user?.username?.charAt(0) || '?'}
                                  </span>
                                </div>
                                <span>{abandonment.user?.username || 'غير معروف'}</span>
                              </div>
                            </td>
                            <td>{abandonment.user?.mobile || 'غير معروف'}</td>
                            <td>
                              <Badge color="info" pill>
                                {abandonment.items?.length || 0}
                              </Badge>
                            </td>
                            <td className="fw-bold">{abandonment.totalAmount || 0} ر.س</td>
                            <td>
                              <div className="d-flex align-items-center">
                                <FiClock className="me-2 text-muted" />
                                {formatArabicDate(abandonment.updatedAt)}
                              </div>
                            </td>
                            <td>
                              {abandonment.lastReminderSent ? (
                                <Badge color="success" className="font-size-12">
                                  {formatArabicDate(abandonment.lastReminderSent)}
                                </Badge>
                              ) : (
                                <Badge color="warning" className="font-size-12">
                                  لم يتم الإرسال
                                </Badge>
                              )}
                              {!canSendReminder(abandonment) && (
                                <div className="mt-1 text-muted" style={{ fontSize: 12 }}>
                                  التالي: {formatArabicDate(nextAllowedAt(abandonment))}
                                </div>
                              )}
                            </td>
                            <td>
                              <Button
                                color="primary"
                                size="sm"
                                className="d-flex align-items-center"
                                onClick={() => handleSendReminder(abandonment)}
                                disabled={sendingReminders[abandonment.cartId] || !canSendReminder(abandonment)}
                                id={`sendBtn-${abandonment.cartId}`}
                              >
                                {sendingReminders[abandonment.cartId] ? (
                                  <Spinner size="sm" className="me-2" />
                                ) : (
                                  <FiSend className="me-2" />
                                )}
                                إرسال تذكير
                              </Button>
                              <UncontrolledTooltip placement="top" target={`sendBtn-${abandonment.cartId}`}>
                                {canSendReminder(abandonment)
                                  ? "إرسال تذكير إلى العميل لإكمال عملية الشراء"
                                  : `لا يمكن الإرسال الآن. التالي: ${formatArabicDate(nextAllowedAt(abandonment))}`}
                              </UncontrolledTooltip>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Message Editor Modal */}
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} centered>
        <ModalHeader toggle={() => setModalOpen(false)}>
          <FiSend className="me-2" />
          إرسال تذكير
        </ModalHeader>
        <ModalBody>
          {currentCart && (
            <div className="mb-4 p-3 bg-light rounded">
              <h6>تفاصيل العربة:</h6>
              <p className="mb-1">
                <strong>العميل:</strong> {currentCart.user?.username || 'غير معروف'}
              </p>
              <p className="mb-1">
                <strong>عدد العناصر:</strong> {currentCart.items?.length || 0}
              </p>
              <p className="mb-0">
                <strong>المبلغ الإجمالي:</strong> {currentCart.totalAmount || 0} ر.س
              </p>
            </div>
          )}
          
          <FormGroup>
            <Label className="fw-bold">رسالة التذكير</Label>
            <div className="mb-3">
              <FormGroup check>
                <Input
                  type="radio"
                  name="messageType"
                  id="defaultMessage"
                  checked={useDefaultMessage}
                  onChange={() => setUseDefaultMessage(true)}
                />
                <Label check for="defaultMessage" className="fw-medium">
                  استخدام الرسالة الافتراضية
                </Label>
              </FormGroup>
              <div className="mt-2 p-3 bg-light rounded border">
                {defaultMessage}
              </div>
            </div>
            
            <div>
              <FormGroup check>
                <Input
                  type="radio"
                  name="messageType"
                  id="customMessage"
                  checked={!useDefaultMessage}
                  onChange={() => setUseDefaultMessage(false)}
                />
                <Label check for="customMessage" className="fw-medium">
                  كتابة رسالة مخصصة
                </Label>
              </FormGroup>
              {!useDefaultMessage && (
                <Input
                  type="textarea"
                  rows="4"
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  placeholder="أدخل رسالة التذكير المخصصة هنا..."
                  className="mt-2"
                />
              )}
            </div>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setModalOpen(false)}>
            إلغاء
          </Button>
          <Button color="primary" onClick={confirmSendReminder}>
            <FiSend className="me-2" />
            إرسال التذكير
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default CartAbandonmentPage;